import { useState, useEffect } from 'react'
import { Link, Outlet, useParams, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  BarChart3,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Users,
  Package,
  Activity,
  Zap,
  Code2,
  Box,
  Bell,
  Shield,
  LogOut,
  User as UserIcon,
  Key,
  BookOpen
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { getFirebaseDb } from '@/lib/firebase'
import { doc, getDoc, collection, collectionGroup, query, where, getDocs } from 'firebase/firestore'
import { CommandPalette } from '@/components/CommandPalette'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { FeatureOverrides, isFeatureEnabled, PlatformSettings } from '@/lib/feature-flags'

interface Organization {
  id: string
  name: string
  slug: string
  logoUrl?: string | null
  enabledFeatures?: string[] // Legacy
  featureOverrides?: FeatureOverrides
}

interface UserOrganization {
  id: string
  name: string
  slug: string
  role: string
}

export interface OrganizationContext {
  organizationId: string | null
  userRole: 'admin' | 'owner' | 'member' | 'super-admin' | null
  isOrgAdmin: boolean
  isSuperAdmin: boolean
}

const navigationItems = [
  {
    title: 'Dashboard',
    href: '/assist',
    icon: LayoutDashboard,
    description: 'Overview and analytics'
  },
  {
    title: 'Recommendation Engine',
    href: '/assist/recommendations',
    icon: Sparkles,
    description: 'Smart recommendations',
    featureId: 'recommendation-engine',
    children: [
      { title: 'Dashboard', href: '/assist/recommendations' },
      { title: 'Users', href: '/assist/recommendations/users', icon: Users },
      { title: 'Recommenders', href: '/assist/recommendations/recommenders', icon: Sparkles },
      { title: 'Products', href: '/assist/recommendations/products', icon: Package }
    ]
  },
  {
    title: 'Signals',
    href: '/assist/signals',
    icon: Activity,
    description: 'Event tracking & data',
    children: [
      { title: 'Overview', href: '/assist/signals' },
      { title: 'Models', href: '/assist/signals/models', icon: Box },
      { title: 'Code', href: '/assist/signals/code', icon: Code2 }
    ]
  },
  {
    title: 'Analytics',
    href: '/assist/analytics',
    icon: BarChart3,
    description: 'Performance insights',
    featureId: 'analytics'
  },
  {
    title: 'Docs',
    href: '/assist/docs',
    icon: BookOpen,
    description: 'Knowledge base'
  }
]

const secondaryNavigationItems = [
  {
    title: 'Settings',
    href: '/assist/settings',
    icon: Settings,
    description: 'Platform configuration'
  },
  {
    title: 'Help',
    href: '/assist/help',
    icon: HelpCircle,
    description: 'Documentation and support'
  }
]

export function AssistPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [userOrganizations, setUserOrganizations] = useState<UserOrganization[]>([])
  const [isAdmin, setIsAdmin] = useState(false)
  const [userRole, setUserRole] = useState<'admin' | 'owner' | 'member' | 'super-admin' | null>(null)
  const [loading, setLoading] = useState(true)
  const [platformDefaults, setPlatformDefaults] = useState<{ [key: string]: boolean }>({})

  useEffect(() => {
    const fetchOrganization = async () => {
      if (!orgId || !user) return
      
      try {
        const db = getFirebaseDb()
        
        // Check if super admin FIRST - force token refresh to get latest claims
        const tokenResult = await user.getIdTokenResult(true)
        const isSuperAdmin = tokenResult.claims.is_super_admin === true
        setIsAdmin(isSuperAdmin)
        
        // Fetch platform defaults (super admin only, but fail silently for others)
        try {
          const platformDoc = await getDoc(doc(db, 'platform', 'settings'))
          if (platformDoc.exists()) {
            const platformData = platformDoc.data() as PlatformSettings
            setPlatformDefaults(platformData.featureDefaults || {})
          }
        } catch {
          // Platform settings not accessible for non-super-admins
        }
        
        // For super admins, fetch organization by slug directly (they have full access)
        if (isSuperAdmin) {
          const orgsRef = collection(db, 'organizations')
          const q = query(orgsRef, where('slug', '==', orgId))
          const snapshot = await getDocs(q)
          
          if (!snapshot.empty) {
            const orgDoc = snapshot.docs[0]
            setOrganization({
              id: orgDoc.id,
              ...orgDoc.data()
            } as Organization)
          }
          
          // Super admins can see all organizations
          const allOrgsSnapshot = await getDocs(collection(db, 'organizations'))
          const orgs: UserOrganization[] = allOrgsSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name,
            slug: doc.data().slug,
            role: 'super-admin'
          }))
          setUserOrganizations(orgs)
          setUserRole('super-admin')
        } else {
          // For regular users: fetch memberships FIRST, then get org data by ID
          // This avoids the permission issue with querying orgs by slug
          try {
            const membersGroup = collectionGroup(db, 'members')
            const memberQ = query(membersGroup, where('userId', '==', user.uid))
            const memberSnapshot = await getDocs(memberQ)
            
            const orgs: UserOrganization[] = []
            let currentOrg: Organization | null = null
            
            for (const memberDoc of memberSnapshot.docs) {
              const membership = memberDoc.data()
              // Path is: organizations/{orgId}/members/{memberId}
              const memberOrgId = memberDoc.ref.parent.parent?.id
              if (!memberOrgId) continue
              
              // Read the org by ID (this works because we know the exact doc ID)
              const orgDocRef = doc(db, 'organizations', memberOrgId)
              const orgSnap = await getDoc(orgDocRef)
              if (orgSnap.exists()) {
                const orgData = orgSnap.data()
                orgs.push({
                  id: orgSnap.id,
                  name: orgData.name,
                  slug: orgData.slug,
                  role: membership.role
                })
                
                // If this is the current org (matching slug), set it
                if (orgData.slug === orgId) {
                  currentOrg = {
                    id: orgSnap.id,
                    name: orgData.name,
                    slug: orgData.slug,
                    logoUrl: orgData.logoUrl,
                    enabledFeatures: orgData.enabledFeatures,
                    featureOverrides: orgData.featureOverrides
                  }
                  setUserRole(membership.role as 'admin' | 'member')
                }
              }
            }
            
            setUserOrganizations(orgs)
            if (currentOrg) {
              setOrganization(currentOrg)
            }
          } catch (memberError) {
            console.warn('Could not fetch member organizations:', memberError)
            setUserOrganizations([])
          }
          
          // Fallback: check Firestore for super admin role
          try {
            const userDoc = await getDoc(doc(db, 'users', user.uid))
            if (userDoc.exists()) {
              const userData = userDoc.data()
              if (userData.role === 'super-admin') {
                setIsAdmin(true)
              }
            }
          } catch {
            // User doc not accessible
          }
        }
      } catch (error) {
        console.error('Error fetching organization:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchOrganization()
  }, [orgId, user])

  const isActiveRoute = (href: string) => {
    const fullPath = `/${orgId}${href}`
    return location.pathname === fullPath || location.pathname.startsWith(fullPath + '/')
  }

  const hasFeature = (featureId: string) => {
    // Use new feature flag system
    return isFeatureEnabled(featureId, organization?.featureOverrides || {}, platformDefaults)
  }

  // Compute enabled features array for CommandPalette
  const enabledFeatures = navigationItems
    .filter(item => !item.featureId || hasFeature(item.featureId))
    .map(item => item.featureId)
    .filter((id): id is string => !!id)

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <div className={cn(
        "relative flex flex-col h-screen bg-card border-r transition-all duration-300",
        isCollapsed ? "w-16" : "w-64"
      )}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          {!isCollapsed && (
            <div className="flex items-center space-x-2">
              {organization?.logoUrl ? (
                <img 
                  src={organization.logoUrl} 
                  alt={organization.name} 
                  className="w-8 h-8 rounded-lg object-cover"
                />
              ) : (
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <h2 className="text-lg font-semibold truncate max-w-[140px]">
                  {organization?.name || 'Logg'}
                </h2>
              </div>
            </div>
          )}
          {isCollapsed && (
            organization?.logoUrl ? (
              <img 
                src={organization.logoUrl} 
                alt={organization.name} 
                className="w-8 h-8 rounded-lg object-cover mx-auto"
              />
            ) : (
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mx-auto">
                <Zap className="w-5 h-5 text-white" />
              </div>
            )
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={cn("h-8 w-8", isCollapsed && "mx-auto mt-2")}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-4">
          <nav className="space-y-2 px-3">
            {navigationItems.map((item) => {
              const isActive = isActiveRoute(item.href)
              const Icon = item.icon
              
              // Check if feature is enabled
              if (item.featureId && !hasFeature(item.featureId)) {
                return null
              }
              
              return (
                <div key={item.href}>
                  <Link
                    to={`/${orgId}${item.href}`}
                    className={cn(
                      "flex items-center space-x-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                      isActive && "bg-accent text-accent-foreground font-medium",
                      isCollapsed && "justify-center"
                    )}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!isCollapsed && (
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{item.title}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {item.description}
                        </div>
                      </div>
                    )}
                  </Link>
                  
                  {/* Sub-navigation */}
                  {!isCollapsed && item.children && isActive && (
                    <div className="ml-6 mt-1 space-y-1 border-l border-border pl-4">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon
                        return (
                          <Link
                            key={child.href}
                            to={`/${orgId}${child.href}`}
                            className={cn(
                              "flex items-center space-x-2 rounded-md px-3 py-1.5 text-xs transition-colors hover:bg-accent hover:text-accent-foreground",
                              location.pathname === `/${orgId}${child.href}` && "bg-accent text-accent-foreground font-medium"
                            )}
                          >
                            {ChildIcon && <ChildIcon className="h-3 w-3" />}
                            <span>{child.title}</span>
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>
          
          {/* Secondary Navigation */}
          <div className="mt-4 px-3">
            <div className="border-t pt-4">
              <nav className="space-y-2">
                {secondaryNavigationItems.map((item) => {
                  const isActive = isActiveRoute(item.href)
                  const Icon = item.icon
                  
                  return (
                    <Link
                      key={item.href}
                      to={`/${orgId}${item.href}`}
                      className={cn(
                        "flex items-center space-x-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                        isActive && "bg-accent text-accent-foreground font-medium",
                        isCollapsed && "justify-center"
                      )}
                    >
                      <Icon className="h-5 w-5 shrink-0" />
                      {!isCollapsed && (
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{item.title}</div>
                        </div>
                      )}
                    </Link>
                  )
                })}
              </nav>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-16 items-center justify-between px-6">
            {/* Left side - Org Switcher + Search */}
            <div className="flex items-center space-x-4">
              {organization && userOrganizations.length > 1 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="w-[200px] justify-between">
                      {organization.name}
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[200px]">
                    <DropdownMenuLabel>Switch Organization</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {userOrganizations.map((org) => (
                      <DropdownMenuItem
                        key={org.id}
                        onClick={() => navigate(`/${org.slug}/assist`)}
                      >
                        {org.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <CommandPalette enabledFeatures={enabledFeatures} />
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center space-x-2">
              {/* Super Admin Link */}
              {isAdmin && (
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/admin" className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Admin
                  </Link>
                </Button>
              )}

              {/* Notifications */}
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 h-3 w-3 bg-destructive rounded-full text-[10px] text-destructive-foreground flex items-center justify-center">
                  3
                </span>
              </Button>

              {/* User Dropdown */}
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.photoURL || ''} alt={user.displayName || ''} />
                        <AvatarFallback>
                          {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount>
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">{user.displayName}</p>
                        <p className="text-xs leading-none text-muted-foreground">
                          {user.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to={`/${orgId}/assist/profile`}>
                        <UserIcon className="mr-2 h-4 w-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    {(isAdmin || userRole === 'admin') && (
                      <DropdownMenuItem asChild>
                        <Link to={`/${orgId}/assist/settings?tab=api-keys`}>
                          <Key className="mr-2 h-4 w-4" />
                          API Keys
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin">
                            <Shield className="mr-2 h-4 w-4" />
                            Super Admin Panel
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet context={{
            organizationId: organization?.id || null,
            userRole: isAdmin ? 'super-admin' : userRole,
            isOrgAdmin: isAdmin || userRole === 'admin' || userRole === 'owner',
            isSuperAdmin: isAdmin
          } satisfies OrganizationContext} />
        </main>
      </div>
    </div>
  )
}
